import type { ApiClient } from '../api';
import { BRAND_META } from '../brand';
import { compact } from '../format';
import { usePoll } from '../hooks';
import type { AppDownloadsApp, AppDownloadsResponse, BrandName } from '../types';
import { Sparkline } from './Sparkline';

/**
 * App downloads (Matthew, 24 October 2026: "now that the UK apps are fully
 * in the app store, we need the apps download metrics added to ACCRA").
 * Every app in brand/properties.json with the stores' own figures: total
 * to date, the newest day the store has reported, the last 7 days and a
 * 30-day trend, plus a total per store front. An app with no figures says
 * why, from the collector's last read: no store key yet, a refusal, or
 * not read yet. The stores report a day or two late, and the panel says
 * which day a figure is for rather than calling it today's.
 */

const STORE: Record<AppDownloadsApp['store'], { name: string; key: string }> = {
  app_store: { name: 'App Store', key: 'Apple' },
  play_store: { name: 'Google Play', key: 'Google' },
};

export const dayLabel = (day: string) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** Live first opens per store front, iPhone and Android together. */
export function liveByMarket(live: NonNullable<AppDownloadsResponse['live']>): { market: string; today: number; last7: number }[] {
  const by = new Map<string, { market: string; today: number; last7: number }>();
  for (const l of live) {
    const m = by.get(l.market) ?? { market: l.market, today: 0, last7: 0 };
    m.today += l.today;
    m.last7 += l.last7;
    by.set(l.market, m);
  }
  return [...by.values()];
}

/** Why an app shows no figures, in plain words. */
export function waitingReason(a: AppDownloadsApp): string {
  const s = a.status;
  if (!s) return 'Waiting for the first store read (once a day, early evening).';
  if (s.state === 'no_key') return `Waiting for the ${STORE[a.store].key} store key to be added.`;
  if (s.state === 'failed') return `The store refused the last read${s.reason ? `: ${s.reason}` : '.'}`;
  return 'Connected. The store has not reported a settled day yet.';
}

export function AppDownloads({ api, brand }: { api: ApiClient; brand?: BrandName }) {
  const res = usePoll<AppDownloadsResponse>(
    () => api.get(`/api/app-downloads${brand ? `?brand=${brand}` : ''}`),
    // A minute, for the live installs; the store figures change daily.
    60_000,
    ['app-downloads', brand],
  );
  const apps = res.data?.apps ?? [];
  if (apps.length === 0) return null;
  const markets = res.data?.markets ?? [];
  const live = liveByMarket(res.data?.live ?? []);
  return (
    <section className="cc-apps" aria-label="App downloads">
      <div className="card">
        <div className="card-head">
          <span className="card-title">📱 App downloads</span>
          <span className="card-note">App Store and Google Play reports · a day or two behind</span>
        </div>
        {live.length > 0 && (
          <p className="cc-apps-live">
            <span className="live-pip" aria-hidden /> Live installs today:{' '}
            {live.map((l) => `${l.market} ${compact(l.today)}`).join(' · ')}
            <span className="cc-apps-live-note"> · the app's first open, as it happens</span>
          </p>
        )}
        {markets.length > 0 && (
          <div className="cc-apps-markets">
            {markets.map((m) => (
              <div key={m.market} className="cc-apps-market">
                <div className="label">{m.market} apps</div>
                <div className="value">{compact(m.total)}</div>
                <div className="sub">
                  {m.last7 > 0 ? `▲${compact(m.last7)} in the last 7 days` : 'none in the last 7 days'}
                  {m.latestDay ? ` · to ${dayLabel(m.latestDay)}` : ''}
                  {m.partial ? ' · counted since launch month' : ''}
                </div>
              </div>
            ))}
          </div>
        )}
        <ul className="cc-apps-list">
          {apps.map((a) => (
            <AppRow key={a.id} a={a} />
          ))}
        </ul>
      </div>
    </section>
  );
}

function AppRow({ a }: { a: AppDownloadsApp }) {
  const meta = BRAND_META[a.brand];
  return (
    <li className="cc-apps-row">
      <div className="cc-apps-name">
        <strong>{a.label}</strong>
        <span>
          {STORE[a.store].name}
          {a.live && a.live.last7 > 0 ? ` · ${compact(a.live.today)} installed today (live)` : ''}
        </span>
      </div>
      {a.total === null ? (
        <p className="cc-apps-waiting">{waitingReason(a)}</p>
      ) : (
        <>
          <dl className="cc-apps-stats">
            <div>
              <dt>{a.basis === 'lifetime' ? 'Total' : `Since ${dayLabel(a.since!)}`}</dt>
              <dd>{compact(a.total)}</dd>
            </div>
            <div>
              <dt>{dayLabel(a.latest!.day)}</dt>
              <dd>{compact(a.latest!.downloads)}</dd>
            </div>
            <div>
              <dt>Last 7 days</dt>
              <dd>{compact(a.last7 ?? 0)}</dd>
            </div>
          </dl>
          <div className="cc-apps-trend">
            {a.daily.length >= 2 && <Sparkline points={a.daily.map((d) => d.downloads)} color={meta.series} width={160} height={36} />}
          </div>
          {a.status?.state === 'failed' && <p className="cc-apps-waiting">{waitingReason(a)}</p>}
        </>
      )}
    </li>
  );
}
